import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { RELATIVE_TIME_RANGES } from '@nirvana/shared/constants';
import { ColumnDetails, ColumnType, CompanyColumnDetails } from '../../types/common';
import { Filter } from '../../gridFilters/types';
import { timeRangeToReportingInterval } from '@nirvana/shared/time';
import { BaseFilterConfig, ExactMatchCustomValues, ExtJsFilter, FilterInstance, FilterKind } from './types';
import { inFilter } from './filterKinds/inFilter';
import { containsFilter } from './filterKinds/containsFilter';
import { startsWithFilter } from './filterKinds/startsWith';
import { timeRangeFilter } from './filterKinds/timeRangeFilter';
import { regexFilter } from './filterKinds/regexFilter';
import { COMPLEX_SEARCH_FILTERS } from '../../../edr/incidentsGrid/types';
import { isPlainObject } from 'lodash';

const isEqual = require('lodash.isequal');
const MILLISECONDS_IN_A_DAY: number = 1000 * 60 * 60 * 24;

const filterKindsByName: { [name: string]: FilterKind } = {
  in: inFilter,
  contains: containsFilter,
  startsWith: startsWithFilter,
  regex: regexFilter,
  timeRange: timeRangeFilter,
};

export function filterRows<ColKeys extends string, Row extends Record<string, any>>(
  filters: Array<FilterInstance<ColKeys>>,
  rows: Array<Row>,
  dropOnMissingCol: boolean = true
): Array<Row> {
  return rows.filter((row: Row) => {
    for (const filter of filters) {
      const colKey = Object.keys(filter)[0];

      const filterName = Object.keys(filter[colKey])[0];

      if (dropOnMissingCol && !(colKey in row)) {
        return false;
      }

      if (!filterKindsByName[filterName].filterInFrontend(filter, row)) {
        return false;
      }
    }

    return true;
  });
}

export function getColumnKeyForFilter<ColumnKeys extends string>(filter: Filter<ColumnKeys>): ColumnKeys {
  const filterKeys = Object.keys(filter);
  if (filterKeys.length !== 1) {
    // Each filter should contain exactly one key for the column.
    throw new Error(`Unexpected filter: ${JSON.stringify(filter, null, 2)}`);
  }
  return filterKeys[0] as ColumnKeys;
}

export function getFilterWithDefaultValues<Column extends number, ColumnKeys extends string>(
  columnDetails: BaseFilterConfig
): Filter<ColumnKeys> {
  const filter: Filter<ColumnKeys> = {};

  switch (columnDetails.type) {
    case ColumnType.DATE:
      // Use this value for the actual moment to not crash the frontend when fitlers are updated
      const safeFilterValue = {
        timeRange: {
          kind: TimeRangeKind.RELATIVE_TIME_RANGE,
          relative: RELATIVE_TIME_RANGES.LAST_24_HOURS,
        },
      };
      filter[columnDetails.key] = columnDetails.filterResetValue || safeFilterValue;
      break;
    case ColumnType.ENUM:
    case ColumnType.TAGS:
    case ColumnType.EDITABLE_SELECT:
      // The default value for ENUM is all possible values - this will match all records.
      let enumValue = columnDetails.values;
      if (columnDetails.filterResetValue && Array.isArray(columnDetails.filterResetValue)) {
        enumValue = columnDetails.filterResetValue;
      }
      /*
        We need this because a filterReset value can be an array like [1,2,3], but also an object like {in: [1,2,3]}
      */
      if (isPlainObject(columnDetails.filterResetValue)) {
        filter[columnDetails.key] = columnDetails.filterResetValue;
      } else {
        filter[columnDetails.key] = { in: (enumValue || []).map(value => value.value) };
      }
      break;
    case ColumnType.STRING:
      // The default value for STRING is contains empty string - this will match all records.
      filter[columnDetails.key] = columnDetails.filterResetValue || { contains: '' };
      break;
    case ColumnType.NUMBER:
      filter[columnDetails.key] = columnDetails.filterResetValue || { eq: '' };
      break;
    case ColumnType.COMPANY:
      if (columnDetails.filterResetValue) {
        filter[columnDetails.key] = {
          in: columnDetails.filterResetValue,
        };
        break;
      }

      filter[columnDetails.key] = {
        in: (columnDetails.values || [])
          .filter(target =>
            (columnDetails as CompanyColumnDetails<Column, ColumnKeys>).defaultTarget.includes(target.value as string)
          )
          .map(target => target.value),
      };
      break;
    case ColumnType.SELECT:
    case ColumnType.COMPLIANCE_SELECT:
      filter[columnDetails.key] = columnDetails.filterResetValue || { value: null };
      break;
    case ColumnType.RANGE:
      const safeFilterRangeValue = {
        range: {
          min: 0,
          max: 100,
        },
      };
      filter[columnDetails.key] = columnDetails.filterResetValue || safeFilterRangeValue;

      break;
    case ColumnType.COMPLEX_SEARCH:
      const safeFilterComplexFilterValue = {
        complexSearch: {
          field: COMPLEX_SEARCH_FILTERS.ALERT_NAME,
          value: '',
        },
      };
      filter[columnDetails.key] = columnDetails.filterResetValue || safeFilterComplexFilterValue;

      break;
    case ColumnType.COMPLEX_ENUM_FILTER:
      const safeFilterComplexEnumFilterValue = {
        complexEnumFilter: [],
      };
      filter[columnDetails.key] = columnDetails.filterResetValue || safeFilterComplexEnumFilterValue;

      break;
    case ColumnType.BACKEND_SINGLE_SELECT:
      filter[columnDetails.key] = columnDetails.filterResetValue || { equals: null };
      break;
    case ColumnType.REMOTE_SELECT:
      filter[columnDetails.key] = columnDetails.filterResetValue || { value: null };
      break;
    default:
      throw new Error(`Unexpected column type ${columnDetails.type}, for column with key ${columnDetails.key}`);
  }

  return filter;
}

export function isDefaultFilter<Column extends number, ColumnKeys extends string>(
  filter: Filter<ColumnKeys>,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  const defaultFilter = getFilterWithDefaultValues<Column, ColumnKeys>(columnDetails);
  return isEqual(filter, defaultFilter);
}

export function sortFiltersByColumnKey<ColumnKeys extends string>(
  filter1: Filter<ColumnKeys>,
  filter2: Filter<ColumnKeys>
): number {
  const columnKey1 = getColumnKeyForFilter<ColumnKeys>(filter1);
  const columnKey2 = getColumnKeyForFilter<ColumnKeys>(filter2);
  if (columnKey1 < columnKey2) {
    return -1;
  } else if (columnKey1 > columnKey2) {
    return +1;
  }
  return 0;
}

export function convertRelativeDateFiltersToAbsolute<ColumnKeys extends string, Column extends number>(
  filter: Filter<ColumnKeys>,
  allColumns: Array<ColumnDetails<Column, ColumnKeys>>,
  timezone: string
): Filter<ColumnKeys> | undefined {
  for (const columnKey of Object.keys(filter)) {
    const column = allColumns.find(col => col.key === columnKey);

    if (column && column.type !== ColumnType.DATE) {
      continue;
    }

    if (
      filter[columnKey].timeRange &&
      filter[columnKey].timeRange.relative &&
      filter[columnKey].timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE
    ) {
      const lastNumDays = filter[columnKey].timeRange.relative.lastNumDays;

      filter[columnKey].timeRange.kind = TimeRangeKind.ABSOLUTE_TIME_RANGE;
      filter[columnKey].timeRange.absolute = timeRangeToReportingInterval(lastNumDays, timezone).current;

      delete filter[columnKey].timeRange.relative;
    }
  }

  return filter;
}

export function isPredefinedFilter<ColumnKeys>(
  columnKey: ColumnKeys,
  predefinedFiltersKeys: Array<ColumnKeys>
): boolean {
  return predefinedFiltersKeys.indexOf(columnKey) >= 0;
}

export function isObject(maybeObject: any): boolean {
  return typeof maybeObject === 'object' && !Array.isArray(maybeObject) && maybeObject !== null;
}

// We need this function because the DAL uses extJS type filters but the gz-grid-view uses
// mongo(-ish) style filters
// TODO implement conversion for all mongo filters that are used
export function convertGridFilterToExtJsFilters<ColKey extends string>(
  gridFilter: Filter<ColKey>
): Array<ExtJsFilter<ColKey>> {
  if (Object.keys(gridFilter).length !== 1) {
    throw new Error('Malformed filter, grid filter must have exactly one property/field');
  }

  const property = Object.keys(gridFilter)[0] as ColKey;

  const filter = gridFilter[property];
  if (!isObject(filter)) {
    return [
      {
        property,
        value: filter,
      },
    ];
  }

  // Check for the number of parameters too, as to crash early
  const nrFilterParams = Object.keys(filter).length;

  if ('exactMatch' in filter) {
    handleExactMatchCustomValues(filter);

    return [
      {
        property: `${property}[exactMatch]`,
        value: filter.exactMatch,
      },
    ];
  }

  // Convert enum filter
  if ('in' in filter && nrFilterParams === 1) {
    if (filter.in.length === 0) {
      return [];
    }

    return [
      {
        property,
        value: filter.in,
      },
    ];
  }

  // Convert single select filter
  if ('equals' in filter && nrFilterParams === 1) {
    if (filter.equals === 0) {
      return [];
    }

    return [
      {
        property,
        value: filter.equals,
      },
    ];
  }

  // Convert string contains filter
  if ('contains' in filter && nrFilterParams === 1) {
    if (filter.contains === '') {
      return [];
    }

    return [
      {
        property,
        value: filter.contains,
      },
    ];
  }

  // Convert string startsWith filter
  if ('startsWith' in filter && nrFilterParams === 1) {
    if (filter.startsWith === '') {
      return [];
    }

    return [
      {
        property,
        value: filter.startsWith,
      },
    ];
  }

  // Convert string regex filter
  if ('regex' in filter && nrFilterParams === 1) {
    if (filter.regex?.pattern === '') {
      return [];
    }

    return [
      {
        property,
        value: filter.regex.pattern,
      },
    ];
  }

  // Convert time range filter
  if ('timeRange' in filter && nrFilterParams === 1) {
    const timeRange = filter.timeRange;
    let maxTime = new Date();
    let minTime: Date | null = null;

    if (timeRange.kind === TimeRangeKind.ALL_TO_DATE) {
      return [];
    }

    if (timeRange.kind === TimeRangeKind.RELATIVE_TIME_RANGE) {
      minTime = new Date(maxTime.getTime() - MILLISECONDS_IN_A_DAY * timeRange.relative.lastNumDays);
    }

    if (timeRange.kind === TimeRangeKind.ABSOLUTE_TIME_RANGE) {
      maxTime = new Date(timeRange.absolute.toTimestamp);
      minTime = new Date(timeRange.absolute.fromTimestamp);
    }

    if (minTime === null) {
      return [];
    }

    return [
      {
        property: `${property}[min]`,
        value: minTime.toISOString(),
      },
      {
        property: `${property}[max]`,
        value: maxTime.toISOString(),
      },
    ];
  }

  if ('value' in filter) {
    return [
      {
        property,
        value: filter.value,
      },
    ];
  }

  if ('eq' in filter) {
    if (filter['eq'] === '') {
      return [];
    }

    return [
      {
        property,
        value: filter['eq'],
      },
    ];
  }

  // Convert range filter
  if ('range' in filter && nrFilterParams === 1) {
    return [
      {
        property,
        value: {
          min: filter.range.min,
          max: filter.range.max,
        },
      },
    ];
  }

  // Convert complex search filter
  if ('complexSearch' in filter && nrFilterParams === 1) {
    return [
      {
        property: filter.complexSearch.field,
        value: filter.complexSearch.value,
      },
    ];
  }

  if ('complexEnumFilter' in filter && nrFilterParams === 1) {
    return [
      {
        property,
        value: filter.complexEnumFilter,
      },
    ];
  }

  throw new Error(`Not implemented/Unknown filter with keys: ${Object.keys(filter)}`);
}

export function convertGridFiltersToExtJsFilters<ColKey extends string>(
  gridFilters: Array<Filter<ColKey>>
): Array<ExtJsFilter<ColKey>> {
  // Each grid filter can be converted into one or more extJs filters (Ex: the timeRange grid filter)
  // and we flatten the list afterwards
  const extJsFilters: Array<ExtJsFilter<ColKey>> = [];

  gridFilters
    .map(convertGridFilterToExtJsFilters)
    .forEach(filters => filters.forEach(filter => extJsFilters.push(filter)));

  return extJsFilters;
}

export function handleExactMatchCustomValues(value) {
  if (value.exactMatch === ExactMatchCustomValues.MY_COMPANY) {
    value.exactMatch = Application.Main.user.company.id;
  }
}
